import { useState, useEffect } from 'react';
import { useQuery, gql, useMutation } from '@apollo/client';
import lodash from 'lodash';

import { useUser } from 'actions/useUser';
import { Role } from 'types/schema.type';
import { AutomaticRpeakAnnotation, RpeakAnnotation, AnnotationValue } from 'types/index';
import { RecordingEcgChart } from './RecordingEcgChart';
import { LoadingSpinner } from './LoadingSpinner';
import { RECORDING_JSON, RECORDING_RPEAKS } from '../queries';
import { RECORDING_APNEA } from '../queries/RECORDING_APNEA';
import { RecordingApneaNotice } from './features/apnea/RecordingApneaNotice';
import { CREATE_POINT_COMMENT, UPDATE_POINT_COMMENT, DELETE_POINT_COMMENT } from '../mutations';
import { useIntervalEdits } from '../actions/useIntervalEdits';
import { withoutDeletedBeats } from '../utilities/withoutDeletedBeats';

export function RecordingChart({ recordingId }: { recordingId: string }) {
  const [localRpeaks, setLocalRpeaks] = useState<RpeakAnnotation[]>([]);
  const { user, loading: userLoading } = useUser();

  const [updateAnnotation] = useMutation(gql`
    mutation Mutation(
      $updateAnalysisRPeakRecordingId: MongoObjectID!
      $updateAnalysisRPeakRpeakSampleIndex: Int!
      $updateAnalysisRPeakValue: AnnotationValue!
    ) {
      updateAnnotation(
        recordingId: $updateAnalysisRPeakRecordingId
        sampleIndex: $updateAnalysisRPeakRpeakSampleIndex
        value: $updateAnalysisRPeakValue
      ) {
        success
        error
      }
    }
  `);

  const [deleteAnnotation] = useMutation(gql`
    mutation Mutation($recordingId: MongoObjectID!, $sampleIndex: Int!) {
      deleteAnnotation(recordingId: $recordingId, sampleIndex: $sampleIndex) {
        success
        error
      }
    }
  `);

  // Noise / AFib edits, with their errors surfaced (project_management#569).
  const intervalEdits = useIntervalEdits(recordingId);

  const [createPointComment] = useMutation(CREATE_POINT_COMMENT, {
    refetchQueries: [
      {
        query: RECORDING_JSON,
        variables: {
          getMyRecordingId: recordingId,
        },
      },
    ],
  });

  const [updatePointComment] = useMutation(UPDATE_POINT_COMMENT, {
    refetchQueries: [
      {
        query: RECORDING_JSON,
        variables: {
          getMyRecordingId: recordingId,
        },
      },
    ],
  });

  const [deletePointComment] = useMutation(DELETE_POINT_COMMENT, {
    refetchQueries: [
      {
        query: RECORDING_JSON,
        variables: {
          getMyRecordingId: recordingId,
        },
      },
    ],
  });

  const { loading, error, data } = useQuery(RECORDING_JSON, {
    variables: {
      getMyRecordingId: recordingId,
    },
  });

  const {
    loading: rpeaksLoading,
    data: rpeaks,
    refetch: refetchRpeaks,
  } = useQuery(RECORDING_RPEAKS, {
    variables: {
      getMyRecordingId: recordingId,
    },
  });

  // project_management#555: the sequence's apnea screening sliced to this
  // recording. Null unless the apnea report is enabled; never blocks the chart.
  const { data: apneaData } = useQuery(RECORDING_APNEA, {
    variables: {
      getMyRecordingId: recordingId,
    },
    errorPolicy: 'ignore',
    skip: !recordingId,
  });
  const apneaScreening = apneaData?.getMyRecording?.apneaScreening ?? null;

  useEffect(() => {
    refetchRpeaks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId]);

  useEffect(() => {
    setLocalRpeaks(rpeaks?.getMyRecording?.analysis?.annotations.map((i) => ({ ...i })));
  }, [recordingId, rpeaks]);

  if (userLoading || !user) return <div>Loading user...</div>;

  const isUserRole = user.role === Role.User;

  if (!recordingId) {
    return <div>Please select a recording</div>;
  }

  if (loading || rpeaksLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    console.log(error);

    return <div>Error while loading data</div>;
  }

  const updateAnnotationHandler = async ({ sampleIndex, value }) => {
    if (isUserRole) return;

    if (value === null) {
      // #188: a new array (lodash.remove in place made React skip the
      // re-render), so the beat's ML label disappears with it. Functional
      // update: the "d" shortcut's GlobalHotKeys keeps the handler from mount,
      // whose `localRpeaks` can be stale.
      setLocalRpeaks((current) => current.filter((rpeak) => rpeak.sampleIndex !== sampleIndex));
      await deleteAnnotation({
        variables: {
          recordingId,
          sampleIndex,
        },
      });
    } else {
      const existingPeak = lodash(localRpeaks).find((i) => sampleIndex === i.sampleIndex);

      if (!existingPeak) {
        localRpeaks.push({
          __typename: 'Annotation',
          sampleIndex,
          annotationValue: AnnotationValue.N,
        });
      } else {
        existingPeak.annotationValue = value;
      }

      setLocalRpeaks(lodash.sortBy(localRpeaks, 'sampleIndex'));

      try {
        await updateAnnotation({
          variables: {
            updateAnalysisRPeakRecordingId: recordingId,
            updateAnalysisRPeakRpeakSampleIndex: sampleIndex,
            updateAnalysisRPeakValue: value,
          },
        });
      } catch (updateError: any) {
        alert(
          `Failed to update annotation. Please contact your administrator or try again. Error: ${
            updateError?.message ?? 'unknown'
          }`,
        );
        throw updateError;
      }
    }

    // await refetchRpeaks();
  };

  const handleCreateNoise = ({ startIndex, endIndex }) => {
    if (isUserRole) return;

    intervalEdits.createNoise({ startIndex, endIndex });
  };

  const handleCreateAFib = ({ startIndex, endIndex }) => {
    if (isUserRole) return;

    intervalEdits.createAFib({ startIndex, endIndex });
  };

  const handleDeleteNoise = (noiseId) => {
    if (isUserRole) return;

    intervalEdits.deleteNoise(noiseId);
  };

  const handleRejectNoise = (noiseId) => {
    if (isUserRole) return;

    intervalEdits.rejectNoise(noiseId);
  };

  const handleRestoreNoise = (noiseId) => {
    if (isUserRole) return;

    const noise = (rpeaks?.getMyRecording?.analysis?.noises ?? []).find((n) => n.id === noiseId);

    if (noise) {
      intervalEdits.restoreNoise({ id: noise.id, startIndex: noise.startIndex, endIndex: noise.endIndex });
    }
  };

  const handleDeleteAFib = (afibId) => {
    if (isUserRole) return;

    intervalEdits.deleteAFib(afibId);
  };

  const handleCreatePointComment = ({ sampleIndex, comment }) => {
    if (isUserRole) return;

    createPointComment({
      variables: {
        input: {
          recordingId,
          sampleIndex,
          comment,
        },
      },
    });
  };

  const handleUpdatePointComment = (pointCommentId) => (newComment) => {
    if (isUserRole) return;

    updatePointComment({
      variables: {
        input: {
          pointCommentId,
          comment: newComment,
        },
      },
    });
  };

  const handleDeletePointComment = (pointCommentId) => () => {
    if (isUserRole) return;

    deletePointComment({
      variables: {
        deletePointCommentId: pointCommentId,
      },
    });
  };

  const handleEditAnnotationInterval = ({ id, startIndex, endIndex }) => {
    if (isUserRole) return;

    intervalEdits.editInterval({ id, startIndex, endIndex });
  };

  if (!localRpeaks) {
    return <div>no rpeaks</div>;
  }

  const sortedNoises = [...(rpeaks?.getMyRecording?.analysis?.noises ?? [])].sort(
    (a, b) => a.startIndex - b.startIndex,
  );

  const sortedAfibs = [...(rpeaks?.getMyRecording?.analysis?.afibs ?? [])].sort(
    (a, b) => a.startIndex - b.startIndex,
  );

  const sortedPointComments = [...(data?.getMyRecording?.pointComments || [])].sort(
    (a, b) => a.sampleIndex - b.sampleIndex,
  );

  /**
   * Prepares x series with indexes based on y series
   */
  function prepData(yAxis: number[]): [number[], number[]] {
    const xAxis = yAxis.map((value, index) => index);

    return [xAxis, yAxis];
  }

  // #188: no ML label on a beat the annotator deleted (chart, jump-to-ML, counts).
  const automaticRpeakAnnotations: AutomaticRpeakAnnotation[] = withoutDeletedBeats(
    rpeaks?.getMyRecording?.analysis?.automaticRpeakAnnotations ?? [],
    localRpeaks,
  );

  return (
    <>
      <RecordingApneaNotice screening={apneaScreening} />
      <RecordingEcgChart
        recordingId={recordingId}
        recordingStartedAt={data.getMyRecording.recordingStartedAt}
        data={{
          ch1: prepData(data.getMyRecording.json.ch1),
        }}
        updateAnnotationHandler={updateAnnotationHandler}
        rpeaks={localRpeaks}
        ppeaks={rpeaks?.getMyRecording?.analysis?.pPeaks || []}
        qpeaks={rpeaks?.getMyRecording?.analysis?.qPeaks || []}
        speaks={rpeaks?.getMyRecording?.analysis?.sPeaks || []}
        tpeaks={rpeaks?.getMyRecording?.analysis?.tPeaks || []}
        automaticRpeakAnnotations={automaticRpeakAnnotations}
        rate={rpeaks?.getMyRecording?.analysis?.rate || []}
        noises={sortedNoises}
        afibs={sortedAfibs}
        pauseEpisodes={rpeaks?.getMyRecording?.analysis?.pauseEpisodes}
        apneaMinutes={apneaScreening?.minutes ?? []}
        pointComments={sortedPointComments}
        comment={data.getMyRecording.comment}
        onCreateNoise={handleCreateNoise}
        onDeleteNoise={handleDeleteNoise}
        onRejectNoise={handleRejectNoise}
        onRestoreNoise={handleRestoreNoise}
        onCreateAFib={handleCreateAFib}
        onDeleteAFib={handleDeleteAFib}
        onUpdatePointComment={handleUpdatePointComment}
        onDeletePointComment={handleDeletePointComment}
        onCreatePointComment={handleCreatePointComment}
        onEditAnnotationInterval={handleEditAnnotationInterval}
      />
      {intervalEdits.feedback}
    </>
  );
}
